/**
 * UMK-envelope AEAD — XChaCha20-Poly1305 via libsodium.
 *
 * Wraps the UMK under a KEK (passphrase / recovery / device / mitid-reserved).
 * Wire format: `version(1) ‖ kind(1) ‖ nonce(24) ‖ ciphertext ‖ tag(16)`.
 * AAD binds `version ‖ kind ‖ userId ‖ envelopeId` so an envelope cannot be
 * replayed across users, kinds, or envelope rows. Spec §2/§5.
 */
import { getSodium } from './sodium.js';
import { randomBytes } from './random.js';
import { VERSION_V1, KindByte, assertHeader } from './version.js';
import { EnvelopeAuthTagMismatchError } from './errors.js';
const NONCE_BYTES = 24; // crypto_aead_xchacha20poly1305_ietf_NPUBBYTES
const TAG_BYTES = 16; // crypto_aead_xchacha20poly1305_ietf_ABYTES
const HEADER_BYTES = 2;
/** AAD = version ‖ kind ‖ userId ‖ envelopeId. */
function envelopeAad(kind, userId, envelopeId) {
    const aad = new Uint8Array(2 + userId.length + envelopeId.length);
    aad[0] = VERSION_V1;
    aad[1] = kind;
    aad.set(userId, 2);
    aad.set(envelopeId, 2 + userId.length);
    return aad;
}
/** Wrap `plaintext` (the UMK) under `kek` into an envelope blob. */
export async function wrapEnvelope(args) {
    const s = getSodium();
    const nonce = randomBytes(NONCE_BYTES);
    const aad = envelopeAad(args.kind, args.userId, args.envelopeId);
    const ctAndTag = s.crypto_aead_xchacha20poly1305_ietf_encrypt(args.plaintext, aad, null, nonce, args.kek);
    const blob = new Uint8Array(HEADER_BYTES + NONCE_BYTES + ctAndTag.length);
    blob[0] = VERSION_V1;
    blob[1] = args.kind;
    blob.set(nonce, HEADER_BYTES);
    blob.set(ctAndTag, HEADER_BYTES + NONCE_BYTES);
    return blob;
}
/**
 * Open an envelope blob back to plaintext. Rejects unknown version/kind BEFORE
 * touching ciphertext; tag failure → {@link EnvelopeAuthTagMismatchError}.
 */
export async function openEnvelope(args) {
    const offset = assertHeader(args.blob, args.expectedKind);
    if (args.blob.length < offset + NONCE_BYTES + TAG_BYTES) {
        throw new EnvelopeAuthTagMismatchError();
    }
    const s = getSodium();
    const nonce = args.blob.subarray(offset, offset + NONCE_BYTES);
    const ctAndTag = args.blob.subarray(offset + NONCE_BYTES);
    const aad = envelopeAad(args.expectedKind, args.userId, args.envelopeId);
    try {
        return s.crypto_aead_xchacha20poly1305_ietf_decrypt(null, ctAndTag, aad, nonce, args.kek);
    }
    catch {
        throw new EnvelopeAuthTagMismatchError();
    }
}
//# sourceMappingURL=envelope.js.map